// src/components/wizard/PostWizard.jsx - ステップ形式の投稿作成ウィザード

import React, { useState } from 'react';
import StepIndicator from './StepIndicator';
import StepInput from './StepInput';
import StepResult from './StepResult';
import StepPost from './StepPost';

const PostWizard = ({
  userPlan,
  usage,
  twitterConnected,
  threadsConnected,
  twitterUsername,
  threadsUsername,
  onGenerate,
  onPostTwitter,
  onPostThreads,
  onPostBoth,
  isLoading,
  isPostingTwitter,
  isPostingThreads,
  onConnectTwitter,
  onConnectThreads,
  onUpgrade,
  onOpenSettings,
  onLogout,
  showToast
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [prompt, setPrompt] = useState('');
  const [tone, setTone] = useState('カジュアル');
  const [generatedPost, setGeneratedPost] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedPost, setEditedPost] = useState('');

  const handleGenerate = async () => {
    const result = await onGenerate(prompt, tone);
    if (result && result.post) {
      setGeneratedPost(result.post);
      setEditedPost(result.post);
      setCurrentStep(2);
    }
  };

  const handleUsePost = () => {
    if (isEditing) {
      setGeneratedPost(editedPost);
    }
    setCurrentStep(3);
  };

  const handlePostSuccess = (platform) => {
    showToast(`${platform}に投稿しました！`, 'success');
  };

  const handleStartOver = () => {
    setCurrentStep(1);
    setPrompt('');
    setGeneratedPost('');
    setEditedPost('');
    setIsEditing(false);
  };

  const currentPost = isEditing ? editedPost : generatedPost;

  return (
    <div className="wizard-container">
      {/* ヘッダー */}
      <header className="wizard-header">
        <div className="wizard-header-inner">
          {/* 左側: ロゴ */}
          <div className="wizard-brand">
            <h1 className="wizard-logo">AiPostPilot</h1>
            <div className="wizard-status">
              <span className={`wizard-plan-badge ${userPlan === 'premium' ? 'premium' : ''}`}>
                {userPlan === 'premium' ? 'Premium' : `Free ${usage.remaining}/3`}
              </span>
              {(twitterConnected || threadsConnected) && (
                <div className="wizard-sns-badges">
                  {twitterConnected && <span className="sns-badge">𝕏</span>}
                  {threadsConnected && <span className="sns-badge">@</span>}
                </div>
              )}
            </div>
          </div>

          {/* 右側: メニューボタン */}
          <div className="wizard-actions">
            <button onClick={onOpenSettings} className="wizard-btn settings">
              設定
            </button>
            <button onClick={onLogout} className="wizard-btn logout">
              ログアウト
            </button>
          </div>
        </div>
      </header>

      {/* メインエリア */}
      <main className="wizard-main">
        {/* PC: サイドバー情報 */}
        <aside className="wizard-sidebar">
          <div className="sidebar-card">
            <h2>AiPostPilot</h2>
            <p>AIがSNS投稿文を作成</p>

            <div className="sidebar-steps">
              <div className="step-item">
                <span className="step-num">1</span>
                <span>テーマを入力</span>
              </div>
              <div className="step-item">
                <span className="step-num">2</span>
                <span>AIが文章を生成</span>
              </div>
              <div className="step-item">
                <span className="step-num">3</span>
                <span>SNSに投稿</span>
              </div>
            </div>

            {userPlan !== 'premium' && (
              <div className="sidebar-upgrade">
                <p>無制限生成 + SNS自動投稿</p>
                <button onClick={onUpgrade}>
                  プレミアム ¥980/月
                </button>
              </div>
            )}
          </div>
        </aside>

        {/* コンテンツエリア */}
        <div className="wizard-content">
          {/* プログレスインジケーター */}
          <StepIndicator currentStep={currentStep} totalSteps={3} />

          {/* ステップコンテンツ */}
          <div className="wizard-step-content">
            {currentStep === 1 && (
              <StepInput
                prompt={prompt}
                setPrompt={setPrompt}
                tone={tone}
                setTone={setTone}
                onGenerate={handleGenerate}
                isLoading={isLoading}
                userPlan={userPlan}
                usage={usage}
                onUpgrade={onUpgrade}
              />
            )}

            {currentStep === 2 && (
              <StepResult
                generatedPost={generatedPost}
                editedPost={editedPost}
                setEditedPost={setEditedPost}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                onUsePost={handleUsePost}
                onRegenerate={() => setCurrentStep(1)}
                showToast={showToast}
              />
            )}

            {currentStep === 3 && (
              <StepPost
                post={currentPost}
                twitterConnected={twitterConnected}
                threadsConnected={threadsConnected}
                twitterUsername={twitterUsername}
                threadsUsername={threadsUsername}
                onPostTwitter={async () => {
                  await onPostTwitter(currentPost);
                  handlePostSuccess('X');
                }}
                onPostThreads={async () => {
                  await onPostThreads(currentPost);
                  handlePostSuccess('Threads');
                }}
                onPostBoth={async () => {
                  await onPostBoth(currentPost);
                  handlePostSuccess('X と Threads');
                }}
                isPostingTwitter={isPostingTwitter}
                isPostingThreads={isPostingThreads}
                onConnectTwitter={onConnectTwitter}
                onConnectThreads={onConnectThreads}
                onStartOver={handleStartOver}
                onBack={() => setCurrentStep(2)}
                userPlan={userPlan}
                onUpgrade={onUpgrade}
                showToast={showToast}
              />
            )}
          </div>
        </div>
      </main>

      <style>{`
        .wizard-container {
          min-height: 100vh;
          background: #0a0f1a;
        }

        /* ヘッダー */
        .wizard-header {
          background: #141b2d;
          border-bottom: 1px solid rgba(255,255,255,0.1);
        }

        .wizard-header-inner {
          max-width: 1200px;
          margin: 0 auto;
          padding: 12px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .wizard-brand {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .wizard-logo {
          background: linear-gradient(90deg, #60a5fa, #34d399);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-size: 18px;
          font-weight: 700;
          margin: 0;
          letter-spacing: -0.02em;
        }

        .wizard-status {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .wizard-plan-badge {
          font-size: 11px;
          padding: 4px 12px;
          border-radius: 10px;
          background: rgba(255,255,255,0.1);
          color: #64748b;
          font-weight: 500;
          letter-spacing: 0.02em;
        }

        .wizard-plan-badge.premium {
          background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%);
          color: #fff;
        }

        .wizard-sns-badges {
          display: flex;
          gap: 4px;
        }

        .sns-badge {
          font-size: 12px;
          background: rgba(59, 130, 246, 0.2);
          padding: 4px 8px;
          border-radius: 10px;
          color: #3b82f6;
          font-weight: 600;
        }

        .wizard-actions {
          display: flex;
          gap: 8px;
        }

        .wizard-btn {
          border: none;
          border-radius: 8px;
          padding: 8px 16px;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.2s;
          font-weight: 500;
        }

        .wizard-btn.settings {
          background: #3b82f6;
          color: #fff;
        }

        .wizard-btn.settings:hover {
          background: #2563eb;
        }

        .wizard-btn.logout {
          background: transparent;
          color: #64748b;
          border: 1px solid rgba(255,255,255,0.1);
        }

        .wizard-btn.logout:hover {
          color: #e2e8f0;
          border-color: rgba(255,255,255,0.2);
        }

        /* メインエリア */
        .wizard-main {
          max-width: 1200px;
          margin: 0 auto;
          padding: 32px 24px;
          display: flex;
          gap: 48px;
        }

        /* サイドバー */
        .wizard-sidebar {
          display: none;
          width: 280px;
          flex-shrink: 0;
        }

        .sidebar-card {
          background: #141b2d;
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 12px;
          padding: 24px;
          position: sticky;
          top: 80px;
        }

        .sidebar-card h2 {
          font-size: 16px;
          font-weight: 700;
          color: #fff;
          margin: 0 0 4px 0;
        }

        .sidebar-card > p {
          font-size: 13px;
          color: #64748b;
          margin: 0 0 24px 0;
        }

        .sidebar-steps {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 24px;
        }

        .step-item {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 13px;
          color: #e2e8f0;
        }

        .step-num {
          width: 24px;
          height: 24px;
          background: #3b82f6;
          color: #fff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 600;
        }

        .sidebar-upgrade {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(16, 185, 129, 0.1));
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 12px;
          padding: 20px;
          text-align: center;
        }

        .sidebar-upgrade p {
          font-size: 12px;
          color: #e2e8f0;
          margin: 0 0 12px 0;
        }

        .sidebar-upgrade button {
          background: #3b82f6;
          color: #fff;
          border: none;
          border-radius: 8px;
          padding: 10px 20px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          width: 100%;
        }

        .sidebar-upgrade button:hover {
          background: #2563eb;
        }

        /* コンテンツエリア */
        .wizard-content {
          flex: 1;
          max-width: 520px;
          margin: 0 auto;
        }

        .wizard-step-content {
          margin-top: 16px;
        }

        /* レスポンシブ */
        @media (max-width: 640px) {
          .wizard-header-inner {
            padding: 10px 16px;
          }

          .wizard-logo {
            font-size: 16px;
          }

          .wizard-brand {
            gap: 12px;
          }

          .wizard-btn {
            padding: 6px 12px;
            font-size: 12px;
          }

          .wizard-main {
            padding: 16px;
          }

          .wizard-content {
            max-width: 100%;
          }
        }

        @media (min-width: 1024px) {
          .wizard-sidebar {
            display: block;
          }

          .wizard-content {
            margin: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default PostWizard;
